import { useEffect, useState } from "react";
import axios, { apiBaseUrl } from "../../api/axios";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import { toast } from 'react-toastify';
import ResponsiveTable from "../../components/ResponsiveTable";
import { EyeIcon, PencilIcon, TrashIcon, CurrencyDollarIcon, CogIcon } from "@heroicons/react/24/outline";
import ExportButtons from "../../components/ExportButtons";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import moment from "moment"
import TransferReversalHistory from "./TransferReversalHistory";

export default function AllTransactions() {
    const navigate = useNavigate();
    const [bankOption, setBankOption] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [showReversalModal, setShowReversalModal] = useState(false);
    const [selectedTransferId, setSelectedTransferId] = useState(null);
    const [filters, setFilters] = useState({
        start: moment().subtract(7, "days").startOf('day').format("YYYY-MM-DD"),
        end: moment().add(1, "days").format("YYYY-MM-DD"),
        bank_id: null,
        type: null
    });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const closeModal = () => setIsModalOpen(false);
    const getAllTransaction = () => {
        axios.get("/bank-transactions/transactions", {
            params: {
                start_date: filters.start,
                end_date: filters.end,
                bank_id: filters.category_id,
                type: filters.type
            }
        }).then((res) => {
            setFiltered(res.data);
        });
    }
    const typeOptions = [
        { value: null, label: "All Type" },
        { value: "deposit", label: "Deposit" },
        { value: "withdraw", label: "Withdraw" },
        { value: "transfer", label: "Transfer" },
        { value: "reversal", label: "Reversal" },
    ];
    const getBankList = () => {
        axios.get("/banks").then((res) => {
            const bankListOption = res.data.map(item => ({ value: item.id, label: item.name }));
            setBankOption([{ value: null, label: "All Bank" }, ...bankListOption]);
        })
            .catch(err => toast.error(err.message || `Error`));
    }
    useEffect(() => {
        getBankList()
        getAllTransaction()
    }, []);

    useEffect(() => {
        getAllTransaction()
    }, [filters]);

    const capitalizeFirstLetter = (word) => {
        if (!word || typeof word !== "string") return word;
        return word.charAt(0).toUpperCase() + word.slice(1);
    };
    const handleExportPDF = () => {
        const pdfContent = filtered.map(s => ({
            ID: s.id,
            Customer: s.customer_name,
            Items: s.items?.map(i => i.name).join(", "),
            Total: s.total,
            Date: s.sale_date,
            Status: s.status,
            Payment: s.payment_status
        }));
        import('jspdf').then(jsPDF => {
            import('jspdf-autotable').then(() => {
                const doc = new jsPDF.jsPDF();
                doc.autoTable({ head: [Object.keys(pdfContent[0]).filter(h => h !== 'actions')], body: pdfContent.map(obj => Object.entries(obj).filter(([k]) => k !== 'actions').map(([, v]) => v)) });

                const date = new Date().toISOString().slice(0, 10);
                const logo = new Image();
                logo.src = "/logo-dark.png";
                logo.onload = () => {
                    doc.addImage(logo, "PNG", 10, 10, 30, 10);
                    doc.setFontSize(14);
                    doc.text("Sales Report", 50, 17);
                    doc.autoTable({
                        startY: 25,
                        head: [Object.keys(pdfContent[0]).filter(h => h !== 'actions')],
                        body: pdfContent.map(obj => Object.entries(obj).filter(([k]) => k !== 'actions').map(([, v]) => v))
                    });
                    doc.save(`Sales_Report_${date}.pdf`);
                };

            });
        });
    }

    const actions = [
        { label: 'Details', value: 'details', icon: <EyeIcon className="w-4 h-4 inline" /> },
        { label: 'History', value: 'history', icon: <CogIcon className="w-4 h-4 inline" /> },
    ];
    const handleActionClick = (action, row) => {
        switch (action) {
            case "details":
                navigate(`/transactions/${row.id}`);
                break;
            case "history":
                setSelectedTransferId(row.id);
                setShowReversalModal(true);
                break;
            default:
                break;
        }
    };
    const handleDownload = (type) => {
        const url = `${apiBaseUrl}/purchases/export/${type}`;
        window.open(url, "_blank");
    };
    const columns = [
        { header: "SL", accessor: "sl", render: (v, row, i) => i + 1 },
        {
            header: "Date", accessor: "transaction_date",
            render: (value, row) => <span>{moment(value).format("DD/MM/YYYY hh:mm:ss A")}</span>
        },
        { header: "Bank Name", accessor: "bank_name" },
        { header: "Description", accessor: "description" },
        {
            header: "Ref. Type", accessor: "reference_type",
            render: (value, row) => <span className=" font-semibold">{capitalizeFirstLetter(value)}</span>
        },
        {
            header: "Type", accessor: "type",
            render: (value, row) => <span className={`${value === 'deposit' ? 'text-green-600' : value === 'withdraw' ? 'text-red-500' : 'text-blue-600'}`}>{capitalizeFirstLetter(value)}</span>
        },
        {
            header: "Amount", accessor: "amount",
            render: (value, row) => <span className={`${row.type === 'deposit' ? 'text-green-600' : row.type === 'withdraw' ? 'text-red-500' : 'text-blue-600'}`}>{value} BDT</span>
        },
        {
            header: "Status", accessor: "status",
            render: (value, row) => <span className=" font-semibold">{capitalizeFirstLetter(value)}</span>
        },
    ];

    return (
        <div className="p-4 max-w-6xl mx-auto text-sm bg-white rounded-md">
            <div className="w-full">
                <ExportButtons title="Transactions List" onDownload={handleDownload} />
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    <div className="flex items-center rounded-md">
                        <Flatpickr
                            style={{
                                backgroundColor: "#fff",
                                opacity: "1",
                                padding: "9px 12px",
                            }}
                            // options={{
                            //   dateFormat: "d-m-Y"
                            // }}
                            value={filters.start}
                            id="date-time-picker"
                            className="form-control"
                            onChange={(date) => setFilters(f => ({ ...f, start: moment(date[0]).format('YYYY-MM-DD') }))}
                        />
                    </div>
                    <div className="flex items-center rounded-md">
                        <Flatpickr
                            style={{
                                backgroundColor: "#fff",
                                opacity: "1",
                                padding: "9px 12px",
                            }}
                            // options={{
                            //   dateFormat: "d-m-Y",
                            // }}
                            value={filters.end}
                            id="date-time-picker"
                            className="form-control"
                            onChange={(date) => setFilters(f => ({ ...f, end: date[0] }))}
                        />
                    </div>
                    <div className="flex items-center rounded-md">
                        <Select
                            name="category"
                            onChange={e => setFilters(f => ({ ...f, category_id: e.value }))}
                            options={bankOption}
                            placeholder="Select Bank"
                            className="w-full"
                            classNamePrefix="react-select"
                            maxMenuHeight={150}
                        />
                    </div>
                    <div className="flex items-center rounded-md">
                        <Select
                            name="type"
                            onChange={e => setFilters(f => ({ ...f, type: e.value }))}
                            options={typeOptions}
                            placeholder="Select Type"
                            className="w-full"
                            classNamePrefix="react-select"
                            maxMenuHeight={150}
                        />
                    </div>
                </div>
                <ResponsiveTable
                    columns={columns}
                    data={filtered}
                    showActions
                    actionButtons={actions}
                    onActionClick={handleActionClick}
                />
            </div>
            {showReversalModal && (
                <TransferReversalHistory transferId={selectedTransferId} onClose={() => setShowReversalModal(false)} />
            )}
        </div>
    );
}